import { useState } from 'react'
import { Plus, Copy, Trash2, Check, RefreshCw } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useUsers, useCreateUser, useDeleteUser, useRegenerateUser } from '@/hooks/useUsers'
import { formatBytes, formatRelativeTime } from '@/lib/utils'
import type { CreateUserResponse, RegenerateUserResponse } from '@/api/types'

export function Users() {
  const { data: users, isLoading, error } = useUsers()
  const createUser = useCreateUser()
  const deleteUser = useDeleteUser()
  const regenerateUser = useRegenerateUser()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [regenerateId, setRegenerateId] = useState<string | null>(null)
  const [newUser, setNewUser] = useState<CreateUserResponse | null>(null)
  const [regeneratedUser, setRegeneratedUser] = useState<RegenerateUserResponse | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    name: '',
    traffic_limit_gb: '',
    expires_days: '',
  })

  const handleCreate = () => {
    const trafficLimit = formData.traffic_limit_gb
      ? parseInt(formData.traffic_limit_gb) * 1024 * 1024 * 1024
      : undefined

    const expiresAt = formData.expires_days
      ? new Date(Date.now() + parseInt(formData.expires_days) * 24 * 60 * 60 * 1000).toISOString()
      : undefined

    createUser.mutate(
      {
        name: formData.name || undefined,
        traffic_limit: trafficLimit,
        expires_at: expiresAt,
      },
      {
        onSuccess: (data) => {
          setNewUser(data)
          setFormData({ name: '', traffic_limit_gb: '', expires_days: '' })
        },
      }
    )
  }

  const withFormat = (url: string, format: string) => {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}format=${format}`
  }

  const handleCopy = async (label: string, value?: string) => {
    if (!value) return
    await navigator.clipboard.writeText(value)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleDelete = () => {
    if (deleteId) {
      deleteUser.mutate(deleteId, {
        onSuccess: () => setDeleteId(null),
      })
    }
  }

  const handleRegenerate = () => {
    if (regenerateId) {
      regenerateUser.mutate(regenerateId, {
        onSuccess: (data) => {
          setRegeneratedUser(data)
          setRegenerateId(null)
        },
      })
    }
  }

  const closeRegenerateDialog = () => {
    setRegeneratedUser(null)
  }

  const closeCreateDialog = () => {
    setIsCreateOpen(false)
    setNewUser(null)
    setFormData({ name: '', traffic_limit_gb: '', expires_days: '' })
  }

  if (error) {
    return (
      <div className="p-6">
        <Header title="Users" />
        <div className="mt-6 rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-destructive">Failed to load users: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Header title="Users" description="Manage subscription users">
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </Header>

      <div className="p-6">
        {isLoading ? (
          <div className="text-muted-foreground">Loading users...</div>
        ) : users?.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h3 className="text-lg font-semibold">No users yet</h3>
            <p className="mt-2 text-muted-foreground">
              Create your first user to generate a subscription URL.
            </p>
            <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {users?.map((user) => {
              const usagePercent = user.traffic_limit
                ? (user.traffic_used / user.traffic_limit) * 100
                : 0
              const isExpired = user.expires_at && new Date(user.expires_at) < new Date()
              const isExpiringSoon =
                user.expires_at &&
                new Date(user.expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)

              return (
                <Card key={user.id}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-base font-medium">
                      {user.name || 'Unnamed User'}
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      {isExpired && <Badge variant="destructive">Expired</Badge>}
                      {!isExpired && isExpiringSoon && <Badge variant="warning">Expiring</Badge>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setRegenerateId(user.id)}
                        title="Regenerate subscription"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(user.id)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Used</span>
                        <div className="font-medium">{formatBytes(user.traffic_used)}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Limit</span>
                        <div className="font-medium">
                          {user.traffic_limit ? formatBytes(user.traffic_limit) : 'Unlimited'}
                        </div>
                      </div>
                    </div>

                    {user.traffic_limit && (
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Usage</span>
                          <span className="font-medium">{usagePercent.toFixed(1)}%</span>
                        </div>
                        <Progress value={usagePercent} />
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground">
                      {user.expires_at ? (
                        <>Expires: {formatRelativeTime(user.expires_at)}</>
                      ) : (
                        <>No expiration</>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => !open && closeCreateDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{newUser ? 'User Created' : 'Create User'}</DialogTitle>
            <DialogDescription>
              {newUser
                ? 'Copy the subscription URL below. This is the only time it will be shown.'
                : 'Add a new subscription user.'}
            </DialogDescription>
          </DialogHeader>

          {newUser ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Subscription URL</label>
                <div className="mt-1 flex gap-2">
                  <Input value={newUser.subscription_url} readOnly className="font-mono text-xs" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy('base', newUser.subscription_url)}
                  >
                    {copied === 'base' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleCopy('singbox', withFormat(newUser.subscription_url, 'singbox'))
                    }
                  >
                    {copied === 'singbox' ? 'Copied Sing-box URL' : 'Copy Sing-box URL'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleCopy(
                        'singbox-apple',
                        withFormat(newUser.subscription_url, 'singbox-apple')
                      )
                    }
                  >
                    {copied === 'singbox-apple'
                      ? 'Copied Sing-box Apple URL'
                      : 'Copy Sing-box Apple URL'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleCopy('clash', withFormat(newUser.subscription_url, 'clash'))
                    }
                  >
                    {copied === 'clash' ? 'Copied Clash URL' : 'Copy Clash URL'}
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild variant="outline">
                    <a
                      href={`clashx://install-config?url=${encodeURIComponent(
                        withFormat(newUser.subscription_url, 'clash')
                      )}&name=${encodeURIComponent('fkgfw')}`}
                    >
                      Import to ClashX
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <a
                      href={`clash://install-config?url=${encodeURIComponent(
                        withFormat(newUser.subscription_url, 'clash')
                      )}&name=${encodeURIComponent('fkgfw')}`}
                    >
                      Import to Clash (Meta)
                    </a>
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={closeCreateDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name (optional)</label>
                  <Input
                    placeholder="User name"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Traffic Limit (GB, optional)</label>
                  <Input
                    type="number"
                    placeholder="e.g., 100"
                    value={formData.traffic_limit_gb}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, traffic_limit_gb: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Expires in (days, optional)</label>
                  <Input
                    type="number"
                    placeholder="e.g., 30"
                    value={formData.expires_days}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, expires_days: e.target.value }))
                    }
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeCreateDialog}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={createUser.isPending}>
                  {createUser.isPending ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? Their subscription will stop working
              immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteUser.isPending}>
              {deleteUser.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <Dialog open={!!regenerateId} onOpenChange={(open) => !open && setRegenerateId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Subscription</DialogTitle>
            <DialogDescription>
              This will generate a new subscription URL and protocol credentials. The old
              subscription URL will stop working immediately. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenerateId(null)}>
              Cancel
            </Button>
            <Button onClick={handleRegenerate} disabled={regenerateUser.isPending}>
              {regenerateUser.isPending ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerated User Dialog */}
      <Dialog open={!!regeneratedUser} onOpenChange={(open) => !open && closeRegenerateDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subscription Regenerated</DialogTitle>
            <DialogDescription>
              Copy the new subscription URL below. The old URL is now invalid.
            </DialogDescription>
          </DialogHeader>
          {regeneratedUser && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">New Subscription URL</label>
                <div className="mt-1 flex gap-2">
                  <Input
                    value={regeneratedUser.subscription_url}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleCopy('regen-base', regeneratedUser.subscription_url)}
                  >
                    {copied === 'regen-base' ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleCopy(
                        'regen-singbox',
                        withFormat(regeneratedUser.subscription_url, 'singbox')
                      )
                    }
                  >
                    {copied === 'regen-singbox' ? 'Copied Sing-box URL' : 'Copy Sing-box URL'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleCopy(
                        'regen-singbox-apple',
                        withFormat(regeneratedUser.subscription_url, 'singbox-apple')
                      )
                    }
                  >
                    {copied === 'regen-singbox-apple'
                      ? 'Copied Sing-box Apple URL'
                      : 'Copy Sing-box Apple URL'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleCopy('regen-clash', withFormat(regeneratedUser.subscription_url, 'clash'))
                    }
                  >
                    {copied === 'regen-clash' ? 'Copied Clash URL' : 'Copy Clash URL'}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={closeRegenerateDialog}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
